<template>
  <div class="wizard-header">
    <div class="flex items-center justify-between">
      <!-- Title and step indicator -->
      <div>
        <p class="text-caption" style="color: var(--text-tertiary);">
          Step {{ currentStep }} of {{ totalSteps }}
        </p>
        <h2 class="heading-3" style="color: var(--text-primary);">
          {{ stepTitle }}
        </h2>
      </div>

      <!-- Close button -->
      <button
        type="button"
        class="wizard-close-btn"
        @click="$emit('close')">
        <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
        <span class="sr-only">Close</span>
      </button>
    </div>

    <!-- Progress bar -->
    <div class="mt-4">
      <div class="progress">
        <div
          class="progress-bar transition-all duration-300"
          :style="{ width: `${progress}%` }" />
      </div>
      <p class="mt-1 text-right text-caption" style="color: var(--text-tertiary);">
        {{ progress }}% complete
      </p>
    </div>

    <!-- Step indicators -->
    <div class="mt-4 flex items-center justify-between">
      <div
        v-for="step in totalSteps"
        :key="step"
        class="flex items-center"
        :class="{ 'flex-1': step < totalSteps }">
        <!-- Step circle -->
        <div
          class="wizard-step-circle"
          :class="getStepCircleClass(step)">
          <svg
            v-if="step < currentStep"
            class="h-4 w-4"
            fill="currentColor"
            viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd" />
          </svg>
          <span v-else>{{ step }}</span>
        </div>

        <!-- Connector line -->
        <div
          v-if="step < totalSteps"
          class="wizard-step-connector"
          :class="{ 'is-completed': step < currentStep }" />
      </div>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  currentStep: { type: Number, required: true },
  totalSteps: { type: Number, required: true },
  stepTitle: { type: String, required: true },
  progress: { type: Number, required: true }
})

defineEmits(['close'])

function getStepCircleClass(step) {
  return {
    'is-active': step <= props.currentStep,
    'is-pending': step > props.currentStep
  }
}
</script>

<style scoped>
.wizard-header {
  border-bottom: var(--border-1) solid var(--border-default);
  padding: var(--space-4) var(--space-6);
  background-color: var(--bg-surface);
}

.wizard-close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-2);
  border-radius: var(--radius-lg);
  color: var(--text-muted);
  transition: all var(--duration-150) var(--ease-in-out);
}

.wizard-close-btn:hover {
  background-color: var(--interactive-hover);
  color: var(--text-secondary);
}

.wizard-step-circle {
  display: flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-full);
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  transition: all var(--duration-150) var(--ease-in-out);
}

.wizard-step-circle.is-active {
  background-color: var(--color-gray-900);
  color: var(--color-white);
}

.wizard-step-circle.is-pending {
  background-color: var(--bg-muted);
  color: var(--text-muted);
}

.wizard-step-connector {
  margin: 0 var(--space-2);
  height: 2px;
  flex: 1;
  background-color: var(--border-default);
  transition: background-color var(--duration-150) var(--ease-in-out);
}

.wizard-step-connector.is-completed {
  background-color: var(--color-gray-900);
}
</style>
